import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { pname } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ProductPicker from "@/components/ProductPicker";
import { Pencil, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

// Sipariş talebi düzenleme: hatalı miktar / yanlış ürün-kod düzeltilir (durum değişmez)
export default function OrderEditDialog({ order, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (order) setF({ product_id: order.product_id || "", product_name: order.product_name || "", brand: order.brand || "", code: order.code || "", requested_qty: order.requested_qty ?? 1, note: order.note || "" });
  }, [order]);
  if (!order || !f) return null;

  const pick = (p) => { setF({ ...f, product_id: p.id, product_name: p.name, brand: p.brand || "", code: p.code || "" }); toast.success("Ürün değiştirildi: " + pname(p)); };
  const save = async () => {
    if (!f.product_name.trim()) return toast.error("Ürün adı gerekli");
    if (!(Number(f.requested_qty) > 0)) return toast.error("Miktar 0'dan büyük olmalı");
    setBusy(true);
    try {
      await api.put(`/orders/${order.id}`, { ...f, requested_qty: Number(f.requested_qty) });
      toast.success("Talep güncellendi"); onSaved && onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="order-edit-dialog">
        <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" />Talebi Düzenle</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2 rounded-lg border bg-muted/30 px-3 py-2">
            <div className="min-w-0 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Ürün</div>
              <div className="font-semibold text-sm break-words" data-testid="order-edit-current">{f.product_name || "—"}</div>
              {f.product_id ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="h-3 w-3" />stoktan</span> : <span className="text-primary">stokta yok (yeni ürün)</span>}
            </div>
            <ProductPicker label="Ürünü Değiştir" onSelect={pick} testid="order-edit-pick" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ürün Adı</Label>
            <Input value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value, product_id: "" })} data-testid="order-edit-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Marka</Label>
              <Input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="order-edit-brand" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ürün Kodu</Label>
              <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="order-edit-code" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Talep Miktarı</Label>
            <Input type="number" min="0" className="tnum" value={f.requested_qty} onChange={(e) => setF({ ...f, requested_qty: e.target.value })} data-testid="order-edit-qty" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Not</Label>
            <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="order-edit-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="order-edit-cancel"><X className="h-4 w-4 mr-1" />İptal</Button>
          <Button onClick={save} disabled={busy} data-testid="order-edit-save">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
